// Predictive Search Web Component
// Handles search autocomplete with keyboard navigation
// Depends on global debounce function from theme.liquid

import { onVendorReady } from '@/utils/vendor-ready'

interface SearchResult {
  // Define based on Shopify's predictive search API response
  [key: string]: any
}

class PredictiveSearch extends HTMLElement {
  private cachedResults: Record<string, SearchResult>
  private input: HTMLInputElement
  private predictiveSearchResults: HTMLElement
  // @ts-ignore - isOpen is set but tracking is done via attribute (kept for potential future use)
  private isOpen: boolean
  private statusElement!: HTMLElement
  private abortController?: AbortController

  constructor() {
    super()
    this.cachedResults = {}
    this.input = this.querySelector('input[type="search"]')!
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]')!
    this.isOpen = false

    this.setupEventListeners()
  }

  private setupEventListeners(): void {
    const form = this.querySelector('form.search') as HTMLFormElement
    form.addEventListener('submit', this.onFormSubmit.bind(this))

    // Use global debounce function from window
    if (typeof window.debounce === 'function') {
      this.input.addEventListener(
        'input',
        window.debounce(() => {
          this.onChange()
        }, 300)
      )
    } else {
      // Fallback if debounce not loaded yet
      this.input.addEventListener('input', this.onChange.bind(this))
    }

    this.input.addEventListener('focus', this.onFocus.bind(this))
    this.addEventListener('focusout', this.onFocusOut.bind(this))
    this.addEventListener('keyup', this.onKeyup.bind(this))
    this.addEventListener('keydown', this.onKeydown.bind(this))
  }

  private getQuery(): string {
    return this.input.value.trim()
  }

  private onChange(): void {
    const searchTerm = this.getQuery()

    if (!searchTerm.length) {
      this.close(true)
      return
    }

    this.getSearchResults(searchTerm)
  }

  private onFormSubmit(event: Event): void {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault()
    }
  }

  private onFocus(): void {
    const searchTerm = this.getQuery()

    if (!searchTerm.length) return

    if (this.getAttribute('results') === 'true') {
      this.open()
    } else {
      this.getSearchResults(searchTerm)
    }
  }

  private onFocusOut(): void {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close(false)
    })
  }

  private onKeyup(event: KeyboardEvent): void {
    if (!this.getQuery().length) this.close(true)
    event.preventDefault()

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break
      case 'ArrowDown':
        this.switchOption('down')
        break
      case 'Enter':
        this.selectOption()
        break
    }
  }

  private onKeydown(event: KeyboardEvent): void {
    // Prevent cursor movement in input with arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault()
    }
  }

  private switchOption(direction: 'up' | 'down'): void {
    if (!this.getAttribute('open')) return

    const moveUp = direction === 'up'
    const selectedElement = this.querySelector('[aria-selected="true"]')
    const allElements = this.querySelectorAll('li')
    let activeElement: Element | null = this.querySelector('li')

    if (moveUp && !selectedElement) return

    this.statusElement.textContent = ''

    if (!moveUp && selectedElement) {
      activeElement =
        selectedElement.nextElementSibling || allElements[0]
    } else if (moveUp) {
      activeElement =
        selectedElement!.previousElementSibling || allElements[allElements.length - 1]
    }

    if (activeElement === selectedElement) return

    activeElement?.setAttribute('aria-selected', 'true')
    selectedElement?.setAttribute('aria-selected', 'false')
  }

  private selectOption(): void {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button')

    if (selectedProduct) {
      ;(selectedProduct as HTMLElement).click()
    }
  }

  private async getSearchResults(searchTerm: string): Promise<void> {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase()

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    // Cancel previous request
    if (this.abortController) {
      this.abortController.abort()
    }

    this.abortController = new AbortController()

    try {
      const routes = (window as any).routes || window.Shopify?.routes
      const searchUrl = routes?.predictive_search_url || '/search/suggest'

      const response = await fetch(
        `${searchUrl}?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[limit]=4&section_id=predictive-search`,
        { signal: this.abortController.signal }
      )

      if (!response.ok) {
        throw new Error(response.statusText)
      }

      const text = await response.text()
      const resultsMarkup = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search')

      this.cachedResults[queryKey] = resultsMarkup as any
      this.renderSearchResults(resultsMarkup as any)
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Predictive search error:', error)
      }
    }
  }

  private renderSearchResults(results: SearchResult): void {
    this.predictiveSearchResults.innerHTML = (results as any).innerHTML
    this.setAttribute('results', 'true')
    this.open()
  }

  open(): void {
    this.setAttribute('open', 'true')
    this.isOpen = true
  }

  close(clearSearchTerm = false): void {
    if (clearSearchTerm) {
      this.input.value = ''
      this.removeAttribute('results')
    }

    this.removeAttribute('open')
    this.isOpen = false
  }
}

// Register custom element only after vendor is ready
// This ensures debounce function is available
onVendorReady(() => {
  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch)
  }
})

export default PredictiveSearch
